import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export type InsertedMessage = {
  id: string;
  conversation_id: string;
  sender_type: string;
  is_private_note: boolean;
};

type MessageInsertListener = (message: InsertedMessage) => void;

const CHANNEL_NAME = "messages-insert-feed";
const TEARDOWN_GRACE_MS = 5_000;

const listeners = new Set<MessageInsertListener>();
let channel: RealtimeChannel | null = null;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;

function dispatch(message: InsertedMessage) {
  for (const listener of [...listeners]) {
    listener(message);
  }
}

function cancelPendingTeardown() {
  if (!teardownTimer) return;
  clearTimeout(teardownTimer);
  teardownTimer = null;
}

function scheduleTeardown() {
  if (teardownTimer) return;
  teardownTimer = setTimeout(() => {
    teardownTimer = null;
    if (listeners.size > 0 || !channel) return;
    supabase.removeChannel(channel);
    channel = null;
  }, TEARDOWN_GRACE_MS);
}

export function subscribeToMessageInserts(listener: MessageInsertListener): () => void {
  listeners.add(listener);
  cancelPendingTeardown();

  if (!channel) {
    channel = supabase
      .channel(CHANNEL_NAME)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        (payload) => dispatch(payload.new as InsertedMessage)
      )
      .subscribe();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    scheduleTeardown();
  };
}
